import React, { Fragment } from 'react'
import { useCroods } from 'croods'
import { Router, Redirect } from '@reach/router'
import moment from 'moment-timezone'
import * as Sentry from '@sentry/browser'
import { isDev } from '@seasonedsoftware/utils'

import { useSyncCookiesWithUser } from 'auth/cookies'
import AuthLayout from 'auth/Layout'
import CheckoutLayout from 'checkout/Layout'
import { AdminRoute, Route } from 'core/Routes'
import { useSiteTitle } from 'utils/hooks'
import useIdentifyUserDeviceSize from 'amplitude/useIdentifyUserDeviceSize'
import useCurrentUser from 'auth/useCurrentUser'
import useUserLeapAttributes from './core/useUserLeapAttributes'

moment.tz.setDefault('America/Los_Angeles')

const Guides = React.lazy(() => import('guides'))
const Admin = React.lazy(() => import('admin/index'))
const Account = React.lazy(() => import('account/Router'))
const NotFound = React.lazy(() => import('core/NotFound'))
const Subscription = React.lazy(() => import('subscription'))
const GumroadConnectAccount = React.lazy(() => import('gumroad/ConnectAccount'))
const GumroadPrivate = React.lazy(() => import('gumroad'))
const Checkout = React.lazy(() => import('checkout'))
const AmazonProposal = React.lazy(() => import('subscription/proposal/amazon'))
const TPTProposal = React.lazy(() => import('subscription/proposal/tpt'))
const BookSuggestions = React.lazy(() => import('bookSuggestions'))
const Survey = React.lazy(() => import('survey/index'))
const StaticPages = React.lazy(() => import('staticPages/List'))
const LiteraryDevices = React.lazy(() => import('staticPages/literaryDevices'))

!isDev() &&
  process.env.REACT_APP_SENTRY_DSN &&
  Sentry.init({ dsn: process.env.REACT_APP_SENTRY_DSN })

const renderRedirects = redirects =>
  redirects.map(redirect => (
    <Fragment key={`${redirect.from}_${redirect.to}`}>
      <Redirect
        from={redirect.from.replace('*', '').replace(':splat', '')}
        to={redirect.to.replace('*', '').replace(':splat', '')}
        noThrow
      />
      <Redirect
        from={redirect.from.replace('*', ':splat')}
        to={redirect.to.replace('*', ':splat')}
        noThrow
      />
    </Fragment>
  ))

const App = props => {
  const [{ currentUser, status, validating }] = useCurrentUser()
  const userLoaded = status !== 'pending' && !validating

  useSiteTitle()
  useIdentifyUserDeviceSize()
  useUserLeapAttributes(currentUser)
  useSyncCookiesWithUser(currentUser, userLoaded)

  const [{ list: redirects }] = useCroods({
    name: 'redirects',
    path: `/redirects?not_optimized`,
    fetchOnMount: true,
    cache: true,
  })

  return (
    <Router className="root-router">
      {renderRedirects(redirects)}
      <Route
        {...props}
        Component={NotFound}
        path="/"
        title="404 - Page not found"
      />
      <Route {...props} Component={Survey} path="/survey" authorize />
      <Route {...props} Component={StaticPages} path="/resources" />
      <Route {...props} Component={LiteraryDevices} path="/literary-devices" />
      <Route {...props} Component={Guides} path="/*" />

      <AdminRoute {...props} Component={Admin} path="/admin/*" title="Admin" />

      <Route {...props} Component={Account} path="/account/*" authorize />

      <Route
        {...props}
        Component={Checkout}
        LayoutComponent={CheckoutLayout}
        path="/checkout/guide/:guideSlug"
        variant={1}
      />

      <Route
        {...props}
        Component={Checkout}
        LayoutComponent={CheckoutLayout}
        path="/checkout/guide-purchase/:guideSlug"
        variant={2}
      />

      <Route
        {...props}
        Component={Checkout}
        LayoutComponent={CheckoutLayout}
        path="/checkout/:planId"
      />

      <Route {...props} Component={AmazonProposal} path="/amazon-subscribe" />
      <Route {...props} Component={TPTProposal} path="/tpt-subscribe" />

      <Route
        {...props}
        Component={Subscription}
        path="/subscription/*"
        authorize
      />
      <Route
        {...props}
        LayoutComponent={AuthLayout}
        Component={GumroadConnectAccount}
        path="/gumroad/connect-account"
      />
      <Route
        {...props}
        Component={GumroadPrivate}
        path="/gumroad/*"
        authorize
      />
      <Route
        {...props}
        Component={BookSuggestions}
        path="/suggest-book-title/*"
      />

      <Route {...props} Component={NotFound} path="/literary-devices/*" />
      <Route {...props} Component={NotFound} path="/resources/*" />

      <Route
        {...props}
        Component={NotFound}
        default
        title="404 - Page not found"
      />
    </Router>
  )
}

export default App
